import * as React from 'react';
import Keycloak from 'keycloak-js';

import { AuthContext } from './AuthContext';
import { IUser } from '../../api/models/IUser';
import { AuthApi } from '../../api/services/auth';
import { Api } from '../../api/Api';
import { App } from '../../App';

// How often to check whether the access token needs refreshing.
const REFRESH_CHECK_INTERVAL_MS = 30_000;
// Refresh the access token if it has fewer than this many seconds left.
const REFRESH_MIN_VALIDITY_S = 70;

// Signing out returns to the landing page at the origin root, not to the app
// under /app/: login-required would send the user straight back to Keycloak's
// sign-in form, which reads as "signing out didn't work". Signing IN needs no
// URL here -- keycloak-js returns to wherever in /app/ the user started.
const siteUrl = () => `${window.location.origin}/`;

interface IProps {
  children?: React.ReactNode;
}

const Auth = (props: IProps) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [user, setUser] = React.useState<IUser | null>(null);
  const keycloakRef = React.useRef<Keycloak | null>(null);
  const refreshTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchMe = async () => {
    try {
      const u = await AuthApi.me();
      setUser({ ...u });
      setIsAuthenticated(true);
    } catch (err) {
      console.error('/me error', err);
    }
  };

  const signout = () => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    setUser(null);
    setIsAuthenticated(false);
    Api.setAccessToken(null);
    (window as any).__bypassUnloadGuard = true;
    keycloakRef.current?.logout({ redirectUri: siteUrl() });
  };

  const accountManagement = () => {
    // keycloak-js's accountManagement() navigates the current tab, which
    // triggers our beforeunload guard and means the user loses their FINS
    // session if they back out. Open the account console in a new tab
    // instead — FINS stays put.
    const url = keycloakRef.current?.createAccountUrl();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  React.useEffect(() => {
    const kc = new Keycloak({
      url: App.config.keycloakURL,
      realm: App.config.keycloakRealm,
      clientId: App.config.keycloakClient,
    });
    keycloakRef.current = kc;

    // login-required may redirect to Keycloak synchronously inside init().
    // Suppress the beforeunload guard for the duration; clear it once init
    // resolves (which only happens if we stayed on this page).
    (window as any).__bypassUnloadGuard = true;

    kc.init({
      onLoad: 'login-required',
      pkceMethod: 'S256',
      checkLoginIframe: false,
    }).then((authenticated) => {
      (window as any).__bypassUnloadGuard = false;
      if (!authenticated) return;

      // Verify the user has at least one role in the FINS client. If not,
      // they signed in to Keycloak successfully but aren't entitled to use
      // this app — log them out.
      const tokenParsed = kc.tokenParsed as { resource_access?: Record<string, { roles?: string[] }> } | undefined;
      const roles = tokenParsed?.resource_access?.[App.config.keycloakClient]?.roles ?? [];
      if (roles.length === 0) {
        console.error('User has no roles for FINS — signing out.');
        (window as any).__bypassUnloadGuard = true;
        kc.logout({ redirectUri: siteUrl() });
        return;
      }

      Api.setAccessToken(kc.token ?? null);
      fetchMe();

      refreshTimerRef.current = setInterval(() => {
        kc.updateToken(REFRESH_MIN_VALIDITY_S).then((refreshed) => {
          if (refreshed) Api.setAccessToken(kc.token ?? null);
        }).catch(() => {
          console.warn('Token refresh failed; redirecting to Keycloak login');
          (window as any).__bypassUnloadGuard = true;
          kc.login();
        });
      }, REFRESH_CHECK_INTERVAL_MS);
    }).catch((err) => {
      (window as any).__bypassUnloadGuard = false;
      console.error('Keycloak init failed', err);
    });

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  return (
    <AuthContext.Provider value={{
      user, setUser,
      isAuthenticated,
      signout,
      accountManagement,
    }}>
      {props.children}
    </AuthContext.Provider>
  );
}

export { Auth }
